import { RefCallback, useCallback, useEffect, useRef } from 'react';

type UseInfiniteScrollProps = {
  fetchMore: () => Promise<unknown>;
};

type UseInfiniteScrollReturn = {
  ref: RefCallback<HTMLElement | null>;
  isFetching: boolean;
};

export const useInfiniteScroll = ({
  fetchMore,
}: UseInfiniteScrollProps): UseInfiniteScrollReturn => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  const fetchMoreRef = useRef(fetchMore);

  // Обновляем ref при изменении fetchMore, чтобы observer всегда вызывал актуальную функцию
  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  }, [fetchMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const [target] = entries;

        if (target.isIntersecting && !isFetchingRef.current) {
          isFetchingRef.current = true;
          fetchMoreRef.current().finally(() => (isFetchingRef.current = false));
        }
      },
      {
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    observerRef.current = observer;

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const ref: RefCallback<HTMLElement | null> = useCallback(
    (node: HTMLElement | null) => {
      if (elementRef.current && observerRef.current) {
        observerRef.current.unobserve(elementRef.current);
      }

      if (node && observerRef.current) {
        requestAnimationFrame(() => {
          observerRef.current?.observe(node);
        });
      }

      elementRef.current = node;
    },
    []
  );

  return { ref, isFetching: isFetchingRef.current };
};
